import React, { useState, useEffect, useCallback } from "react";
import { $getRoot, $createParagraphNode, $createTextNode } from "lexical";
import { ChatTabsDesktop } from "@/components/chat/ChatTabsDesktop";
import { ChatTabsMobile } from "@/components/chat/ChatTabsMobile";
import { NotebookPen, Copy, FileDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNotesEditor } from "@/context/NotesEditorContext";
import { getNotesAsText, exportNotesAsPDF } from "@/lib/notes/exportNotes";
import { NotesPanel } from "@/components/chat/panels/NotesPanel";
import { ChaptersPanel } from "@/components/chat/panels/ChaptersPanel";
import { ContentChatPanel } from "./panels/ContentChatPanel";
import { NotesFloater } from "@/components/notes/NotesFloater";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NotesEditorProvider } from "@/context/NotesEditorContext";
import { ContentProvider } from "./ContentContext";
import { CONTENT_CHAT_TABS } from "./contentChatConfig";
import { ContentFlashcardsPanel } from "./panels/ContentFlashcardsPanel";
import { ContentQuizzesPanel } from "./panels/ContentQuizzesPanel";
import { ContentSummaryPanel } from "./panels/ContentSummaryPanel";
import { usePublicAuth } from "@/context/PublicAuthContext";
import { useContentNavigationOptional } from "@/context/ContentNavigationContext";
import type { ContentItem } from "@/types/content";
import type { ChatTabId } from "@/components/chat/ChatRoot";
import type { QuizHelperRequest } from "@/types/quizHelpers";
import { buildQuizHelperUserMessage } from "@/lib/quizHelperPrompts";

interface ContentChatRootProps {
  content: ContentItem;
  isContentViewerVisible?: boolean;
}

export function ContentChatRoot({ content, isContentViewerVisible = true }: ContentChatRootProps) {
  const allowedTabs = CONTENT_CHAT_TABS[content.type];
  const [activeTab, setActiveTab] = useState<ChatTabId>(allowedTabs?.[0] || "chat");
  const [isFloaterOpen, setIsFloaterOpen] = useState(false);
  const { toast } = useToast();

  return (
    <ContentProvider content={content}>
      <NotesEditorProvider>
        <ContentChatRootInner
          content={content}
          allowedTabs={allowedTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isFloaterOpen={isFloaterOpen}
          setIsFloaterOpen={setIsFloaterOpen}
          toast={toast}
          isContentViewerVisible={isContentViewerVisible}
        />
      </NotesEditorProvider>
    </ContentProvider>
  );
}

interface ContentChatRootInnerProps {
  content: ContentItem;
  allowedTabs: ChatTabId[];
  activeTab: ChatTabId;
  setActiveTab: (tab: ChatTabId) => void;
  isFloaterOpen: boolean;
  setIsFloaterOpen: (open: boolean) => void;
  toast: ReturnType<typeof useToast>["toast"];
  isContentViewerVisible: boolean;
}

function ContentChatRootInner({
  content,
  allowedTabs,
  activeTab,
  setActiveTab,
  isFloaterOpen,
  setIsFloaterOpen,
  toast,
  isContentViewerVisible,
}: ContentChatRootInnerProps) {
  const { editor } = useNotesEditor();
  const { requireAuth, isPublicView } = usePublicAuth();
  const [pendingHelperMessage, setPendingHelperMessage] = useState<string | null>(null);
  const [pendingReplyText, setPendingReplyText] = useState<string | null>(null);
  const navigationContext = useContentNavigationOptional();

  // Register tab switcher with navigation context
  useEffect(() => {
    if (navigationContext) {
      navigationContext.registerTabSwitcher((tab: string) => {
        setActiveTab(tab as ChatTabId);
      });
      return () => navigationContext.registerTabSwitcher(null);
    }
  }, [navigationContext, setActiveTab]);

  // Register chat actions using pending state so they work even when chat tab is unmounted
  useEffect(() => {
    if (navigationContext) {
      navigationContext.registerChatActions({
        sendMessage: (text: string) => {
          setPendingHelperMessage(text);
          setActiveTab("chat" as ChatTabId);
        },
        setReplyText: (text: string) => {
          setPendingReplyText(text);
          setActiveTab("chat" as ChatTabId);
        },
      });
      return () => navigationContext.registerChatActions(null);
    }
  }, [navigationContext, setActiveTab]);

  // Register notes action with navigation context
  useEffect(() => {
    if (navigationContext && editor) {
      navigationContext.registerNotesAction((text: string) => {
        editor.update(() => {
          const root = $getRoot();
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(text));
          root.append(paragraph);
        });
      });
      return () => navigationContext.registerNotesAction(null);
    }
  }, [navigationContext, editor]);

  // Expose setPendingReplyText for chat actions from PDF
  const handleSetPendingReplyText = useCallback((text: string) => {
    setPendingReplyText(text);
  }, []);

  const handleQuizHelperRequest = (req: QuizHelperRequest) => {
    const msg = buildQuizHelperUserMessage(req);
    setPendingHelperMessage(msg);
    setActiveTab("chat");
  };

  const handlePendingMessageConsumed = () => {
    setPendingHelperMessage(null);
  };

  const handleCopyNotes = async () => {
    if (isPublicView) { requireAuth(); return; }
    if (!editor) {
      toast({
        title: "Error",
        description: "Notes editor not ready",
        variant: "destructive"
      });
      return;
    }
    try {
      const text = getNotesAsText(editor);
      if (!text.trim()) {
        toast({
          title: "No content",
          description: "There are no notes to copy",
          variant: "destructive"
        });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: "Notes copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy notes",
        variant: "destructive"
      });
    }
  };

  const handleExportPDF = async () => {
    if (isPublicView) { requireAuth(); return; }
    if (!editor) {
      toast({
        title: "Error",
        description: "Notes editor not ready",
        variant: "destructive"
      });
      return;
    }
    try {
      await exportNotesAsPDF(editor);
      toast({
        title: "Success!",
        description: "Notes exported as PDF"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to export PDF",
        variant: "destructive"
      });
    }
  };

  const handleOpenFloater = () => {
    if (isPublicView) { requireAuth(); return; }
    setIsFloaterOpen(true);
  };

  const handleTabChange = (tab: ChatTabId) => {
    if (isPublicView) { requireAuth(); return; }
    setActiveTab(tab);
  };

  const showFloaterButton = activeTab === "notes";

  return (
    <div className="relative flex h-full w-full flex-col">
      {/* Capture all clicks in public view */}
      {isPublicView && (
        <div 
          className="absolute inset-0 z-10 cursor-pointer" 
          onClick={requireAuth}
          aria-hidden="true"
        />
      )}
      {/* Notes Floater */}
      <NotesFloater
        isOpen={isFloaterOpen}
        onClose={() => setIsFloaterOpen(false)}
        title="Notes"
      />

      {/* Desktop */}
      <div className="hidden h-full w-full md:flex md:flex-col overflow-hidden">
        <div className="flex-shrink-0 flex justify-center pt-3">
          <ChatTabsDesktop activeTab={activeTab} setActiveTab={handleTabChange} allowedTabs={allowedTabs} className="mx-0 px-[6px]" />
        </div>

        {/* Header controls */}
        <div className="flex-shrink-0 flex justify-end px-6 pt-2">
          <TooltipProvider>
            <div className="flex items-center justify-end gap-2">
              {showFloaterButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenFloater}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-auto border-none bg-background/80 px-2 py-1 text-sm text-muted-foreground backdrop-blur-sm"
                    >
                      <span className="flex items-center gap-1">
                        <NotebookPen className="h-4 w-4" />
                        <span>Notes Floater</span>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open Notes Floater</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {activeTab === "notes" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyNotes}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy Notes</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleExportPDF}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download Notes</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-1 flex-1 min-h-0 overflow-hidden">
          {activeTab === "chat" && (
            <ContentChatPanel 
              content={content} 
              onSwitchTab={handleTabChange}
              pendingMessage={pendingHelperMessage}
              onPendingMessageConsumed={handlePendingMessageConsumed}
              pendingReplyText={pendingReplyText}
              onPendingReplyTextConsumed={() => setPendingReplyText(null)}
            />
          )}
          {activeTab === "flashcards" && <ContentFlashcardsPanel contentId={content.id} chatId={content.id} />}
          {activeTab === "quizzes" && <ContentQuizzesPanel contentId={content.id} onHelperRequest={handleQuizHelperRequest} />}
          {activeTab === "chapters" && <ChaptersPanel />}
          {activeTab === "summary" && <ContentSummaryPanel contentId={content.id} />}
          <div className={activeTab === "notes" ? "h-full" : "hidden"}>
            <NotesPanel />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className={`flex ${isContentViewerVisible ? 'h-full' : 'h-[calc(100dvh-96px)]'} flex-col md:hidden`}>
        <div className="px-2 pt-2">
          <ChatTabsMobile activeTab={activeTab} setActiveTab={handleTabChange} allowedTabs={allowedTabs} />
        </div>

        {/* Header controls */}
        <div className="flex justify-end px-4 pt-2">
          <TooltipProvider>
            <div className="sticky top-0 z-20 flex items-center justify-end gap-2">
              {showFloaterButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenFloater}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-auto border-none bg-background/80 px-2 py-1 text-sm text-muted-foreground backdrop-blur-sm"
                    >
                      <span className="flex items-center gap-1">
                        <NotebookPen className="h-4 w-4" />
                        <span>Notes Floater</span>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open Notes Floater</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {activeTab === "notes" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyNotes}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy Notes</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleExportPDF}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download Notes</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-1 flex-1 overflow-hidden">
          {activeTab === "chat" && (
            <ContentChatPanel 
              isMobile 
              content={content} 
              onSwitchTab={handleTabChange}
              pendingMessage={pendingHelperMessage}
              onPendingMessageConsumed={handlePendingMessageConsumed}
              pendingReplyText={pendingReplyText}
              onPendingReplyTextConsumed={() => setPendingReplyText(null)}
            />
          )}
          {activeTab === "flashcards" && <ContentFlashcardsPanel isMobile contentId={content.id} chatId={content.id} />}
          {activeTab === "quizzes" && <ContentQuizzesPanel isMobile contentId={content.id} onHelperRequest={handleQuizHelperRequest} />}
          {activeTab === "chapters" && <ChaptersPanel isMobile />}
          {activeTab === "summary" && <ContentSummaryPanel isMobile contentId={content.id} />}
          <div className={activeTab === "notes" ? "h-full" : "hidden"}>
            <NotesPanel isMobile />
          </div>
        </div>
      </div>
    </div>
  );
}
